import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IoIosArrowBack, IoIosArrowForward, IoMdClose } from 'react-icons/io';
import { MdZoomIn, MdZoomOut } from 'react-icons/md';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';

export const ImageModal = ({ images, currentIndex, onClose }) => {
  const [index, setIndex] = useState(currentIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const imageRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Preload adjacent images
  useEffect(() => {
    const preloadImage = (url) => {
      const img = new Image();
      img.src = url;
    };

    const nextIndex = (index + 1) % images.length;
    const prevIndex = index === 0 ? images.length - 1 : index - 1;

    preloadImage(images[nextIndex].url);
    preloadImage(images[prevIndex].url);
  }, [index, images]);

  // Reset zoom and position when changing images
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsLoading(true);
  }, [index]);

  // Auto-hide controls after inactivity
  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setShowControls(true);
    controlsTimeoutRef.current = setTimeout(() => {
      if (!isDragging && scale === 1) {
        setShowControls(false);
      }
    }, 3000);
  }, [isDragging, scale]);

  useEffect(() => {
    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [resetControlsTimeout]);

  const handleMouseMove = useCallback(() => {
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  // Navigation functions
  const nextImage = useCallback(() => {
    setIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  // Zoom functions
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setScale((prev) => Math.min(Math.max(0.5, prev - e.deltaY * 0.01), 3));
    }
  }, []);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.5, 3));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.5, 0.5));
  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Touch gesture handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: nextImage,
    onSwipedRight: prevImage,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '0') {
        handleResetZoom();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextImage, prevImage, onClose]);

  // Render thumbnails
  const renderThumbnails = () => (
    <div className="flex overflow-x-auto absolute right-0 bottom-0 left-0 justify-center p-4 space-x-2 bg-gradient-to-t from-black to-transparent">
      {images.map((img, i) => (
        <button
          key={i}
          onClick={() => setIndex(i)}
          className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden transition-all duration-200 ${
            i === index
              ? 'ring-2 ring-white scale-110'
              : 'opacity-50 hover:opacity-100'
          }`}>
          <img
            src={img.url}
            alt={`Thumbnail ${i + 1}`}
            className="object-cover w-full h-full"
          />
        </button>
      ))}
    </div>
  );

  return (
    <div
      className="flex fixed inset-0 z-50 justify-center items-center bg-black"
      onMouseMove={handleMouseMove}
      {...swipeHandlers}>
      {/* Background click to close */}
      <div
        onClick={onClose}
        className="absolute inset-0 z-40 bg-black bg-opacity-90 backdrop-blur-sm"
      />

      {/* Modal content */}
      <motion.div
        className="flex relative z-50 justify-center items-center w-full h-full"
        onClick={(e) => e.stopPropagation()}>
        {/* Loading indicator */}
        {isLoading && (
          <div className="flex absolute inset-0 justify-center items-center">
            <div className="w-12 h-12 rounded-full border-4 border-white animate-spin border-t-transparent" />
          </div>
        )}

        {/* Main image */}
        <motion.img
          ref={imageRef}
          src={images[index].url}
          alt={`Image ${index + 1} of ${images.length}`}
          className="max-h-[85vh] max-w-[85vw] object-contain select-none"
          style={{
            cursor: isDragging ? 'grabbing' : scale > 1 ? 'grab' : 'auto'
          }}
          drag={scale > 1}
          dragConstraints={imageRef}
          dragElastic={0.1}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => setIsDragging(false)}
          onWheel={handleWheel}
          animate={{
            scale,
            x: position.x,
            y: position.y,
            opacity: isLoading ? 0 : 1
          }}
          transition={{ duration: 0.3 }}
          onLoad={() => setIsLoading(false)}
        />

        {/* Controls overlay - only show when showControls is true */}
        <AnimatePresence>
          {showControls && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex absolute inset-0 flex-col pointer-events-none">
              {/* Top bar */}
              <div className="flex justify-between items-center p-4 bg-gradient-to-b from-black to-transparent pointer-events-auto">
                <div className="text-white">
                  {index + 1} / {images.length}
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleZoomOut}
                    className="p-2 text-white rounded-full hover:bg-white/20"
                    disabled={scale <= 0.5}>
                    <MdZoomOut size={24} />
                  </button>
                  <button
                    onClick={handleZoomIn}
                    className="p-2 text-white rounded-full hover:bg-white/20"
                    disabled={scale >= 3}>
                    <MdZoomIn size={24} />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 text-white rounded-full hover:bg-white/20">
                    <IoMdClose size={24} />
                  </button>
                </div>
              </div>

              {/* Navigation buttons */}
              <div className="flex flex-1 justify-between items-center px-4 pointer-events-auto">
                <button
                  onClick={prevImage}
                  className="p-2 text-white rounded-full transition-colors bg-black/50 hover:bg-black/70">
                  <IoIosArrowBack size={30} />
                </button>
                <button
                  onClick={nextImage}
                  className="p-2 text-white rounded-full transition-colors bg-black/50 hover:bg-black/70">
                  <IoIosArrowForward size={30} />
                </button>
              </div>

              {/* Thumbnails */}
              {renderThumbnails()}

              {/* Keyboard shortcuts help */}
              <div className="absolute left-4 bottom-24 text-sm text-white/70">
                <div>← → Arrow keys to navigate</div>
                <div>Scroll to zoom • 0 to reset zoom</div>
                <div>ESC to close</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};
